import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { GridSync } from '@grid/models';
import { ConfirmDialogComponent } from '@shared/components';
import { FileUploadService } from '@shared/components/file-upload';
import { StaticMessage } from '@shared/enums';
import { DprAttachment } from '@shared/models/dpr';
import { DprAttachmentsGridData, Sync } from '@shared/models/grid';
import { Constants, getStaticMessage } from '@shared/utils';

@Injectable()
export class DprAttachmentService {
  fileNameText = $localize`:@@dprDetails.dprAttachmentsGrid.txtFileName:File name`;

  constructor(
    private dialog: MatDialog,
    private fileUploadService: FileUploadService
  ) {}

  mapFileToDprAttachmentGridData(
    file: File,
    url: string
  ): DprAttachmentsGridData {
    const fileUpload = this.fileUploadService.processFile({
      id: -1,
      name: file.name,
      type: file.type,
      size: file.size,
      uploadDate: new Date(),
      externalUrl: url,
    });

    const isImage = this.fileUploadService.isImage(file.type);
    fileUpload.isImage = isImage;
    fileUpload.isPrintInReport = isImage;
    const name = this.sanitizeName(fileUpload.name);

    return {
      id: null,
      attachmentId: fileUpload.id,
      name,
      displayName: name,
      isImage,
      isPrintInReport: isImage,
      extension: fileUpload.extension,
      type: fileUpload.type,
      size: fileUpload.size,
      uploadDate: fileUpload.uploadDate,
      externalUrl: fileUpload.externalUrl,
      attachment: null,
    };
  }

  mapToGridData(attachments: DprAttachment[]): DprAttachmentsGridData[] {
    return attachments?.map((x) => {
      const isImage = this.fileUploadService.isImage(x.attachment.type);

      return {
        id: x.id,
        attachmentId: x.attachmentId,
        name: this.sanitizeName(x.attachment.name),
        type: x.attachment.type,
        size: x.attachment.size,
        uploadDate: x.attachment.uploadDate,
        externalUrl: x.attachment.externalUrl,
        displayName: this.sanitizeName(x.attachment.displayName),
        comment: x.attachment.comment,
        isImage,
        isPrintInReport: x.attachment.isPrintInReport,
        extension: this.fileUploadService.getExt(x.attachment.name),
        copyOf: x.copyOf,
        dprAttachmentType: x.attachment.dprAttachmentType,
        dprAttachmentOrder: x.attachment.dprAttachmentOrder,
        dprDetailId: x.attachment.dprDetailId,
        attachment: null,
      };
    });
  }

  mapToAttachments(data: DprAttachmentsGridData[]): DprAttachment[] {
    return data?.map((x) => ({
      id: x.id,
      attachmentId: x.attachmentId,
      attachment: {
        id: x.attachmentId,
        name: this.addExtension(x.name, x.extension),
        type: x.type,
        size: x.size,
        uploadDate: x.uploadDate,
        externalUrl: x.externalUrl,
        displayName: this.addExtension(x.displayName, x.extension),
        comment: x.comment,
        isImage: x.isImage,
        isPrintInReport: x.isPrintInReport,
        extension: x.extension,
        copyOf: x.copyOf,
        dprAttachmentType: x.dprAttachmentType,
        dprAttachmentOrder: x.dprAttachmentOrder,
        dprDetailId: x.dprDetailId,
      },
      copyOf: x.copyOf,
    }));
  }

  /**
   * @description Maps DPR attachment sync to grid data sync object.
   */
  mapSyncDprAttachmentToGridData(
    syncDprAttachment: Sync<DprAttachment>
  ): Sync<DprAttachmentsGridData> {
    return {
      addedList: this.mapToGridData(syncDprAttachment?.addedList),
      updatedList: this.mapToGridData(syncDprAttachment?.updatedList),
      deletedIds: syncDprAttachment?.deletedIds,
    };
  }

  validateChanges(changes: GridSync<DprAttachmentsGridData>): boolean {
    const entities = [
      ...(changes?.addedList ?? []),
      ...(changes?.updatedList ?? []),
    ];

    for (const entity of entities) {
      const errorMessages = [];

      if (String.isNullOrWhiteSpace(entity.name)) {
        errorMessages.push(
          $localize`:@@dprDetails.dprAttachmentsGrid.errFileNameMandatory:- File name field is mandatory.`
        );
      } else {
        if (!this.fileUploadService.validateFileName(entity.name)) {
          errorMessages.push(
            getStaticMessage(
              StaticMessage.FieldNameContainsInvalidCharacters,
              this.fileNameText
            )
          );
        }
      }

      if (errorMessages.length) {
        this.dialog.open(ConfirmDialogComponent, {
          ...Constants.DialogOptions,
          data: {
            extra: $localize`:@@dprDetails.dprAttachmentsGrid.titleUploadFiles:Upload files`,
            title: getStaticMessage(StaticMessage.ValidationErrorTitle),
            message: errorMessages.join('<br>'),
            buttonLabel: getStaticMessage(StaticMessage.OkButton),
            hideCancelButton: true,
          },
        });

        return false;
      }
    }

    return true;
  }

  addExtension(name: string, extension: string): string {
    return extension ? `${name}.${extension}` : name;
  }

  sanitizeName(name: string): string {
    const dotLastIndex = name.lastIndexOf('.');

    return dotLastIndex === -1 ? name : name.slice(0, dotLastIndex);
  }
}
